import type { Schemas } from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { paths } from '../../api/schema';

export type ActivationCode = Schemas['ActivationCode'];
export type CodeStatus = ActivationCode['status'];
export type CreatedCode = ActivationCode & { code: string };
export type CreateCodeBody = Schemas['CreateActivationCodeRequest'];
export type CodeChannel = Schemas['DeliverActivationCodeRequest']['channel'];
export type CodeListQuery = NonNullable<
  paths['/platform/activation-codes']['get']['parameters']['query']
>;

export const codeKeys = {
  all: ['platform', 'activation-codes'] as const,
  list: (q: CodeListQuery) => ['platform', 'activation-codes', q] as const,
  tenantNames: ['platform', 'activation-codes', 'tenant-names'] as const,
};

export const CHANNELS: { value: CodeChannel; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone call' },
  { value: 'in_person', label: 'In person' },
  { value: 'other', label: 'Other' },
];

export const channelLabel = (v: string | null) =>
  v === null ? '—' : (CHANNELS.find((c) => c.value === v)?.label ?? v);

export const CODE_STATUS_OPTIONS: { value: CodeStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'ACTIVE', label: 'Unused' },
  { value: 'REDEEMED', label: 'Redeemed' },
  { value: 'REVOKED', label: 'Revoked' },
  { value: 'EXPIRED', label: 'Expired' },
];

/** Lists codes: one shop's (tenant route) or across the platform (optionally by tenant). */
export function fetchCodes(query: CodeListQuery, tenantScope?: string) {
  if (tenantScope) {
    const rest = { page: query.page, pageSize: query.pageSize, status: query.status };
    return unwrap(
      api.GET('/platform/tenants/{id}/activation-codes', {
        params: { path: { id: tenantScope }, query: rest },
      }),
    );
  }
  return unwrap(api.GET('/platform/activation-codes', { params: { query } }));
}

export function createCode(tenantId: string, body: CreateCodeBody): Promise<CreatedCode> {
  return unwrap(
    api.POST('/platform/tenants/{id}/activation-codes', {
      params: { path: { id: tenantId } },
      body,
    }),
  );
}

export function revokeCode(id: string, reason: string) {
  return unwrap(
    api.POST('/platform/activation-codes/{id}/revoke', {
      params: { path: { id } },
      body: { reason },
    }),
  );
}

export function deliverCode(id: string, channel: CodeChannel) {
  return unwrap(
    api.POST('/platform/activation-codes/{id}/deliver', {
      params: { path: { id } },
      body: { channel },
    }),
  );
}

/** `XXXX-XXXX-XXXX-8RZB` style masked code from the stored last-4 hint. */
export const maskedCode = (hint: string) => `••••-••••-••••-${hint}`;
